import { useNavigate } from 'react-router-dom';
import { ArrowUpDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { fmtDateTime } from '@/lib/datetime';
import { EmptyState } from '@/components/common/EmptyState';
import { JobRowActions } from './JobRowActions';
import { jobStatusLabel, finalDecisionLabel, reviewStatusLabel } from '@/lib/enumLabels';
import type { JobListItem } from '@/hooks/useJobList';
import type { JobStatus, FinalDecision } from '@/types';

// ─── Badge helpers ───────────────────────────────────────────────────────────

const statusColors: Record<JobStatus, string> = {
  pending: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  processing: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
  completed: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300',
  failed: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
  cancelled: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400',
};

const decisionColors: Record<FinalDecision, string> = {
  APPROVED: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300',
  APPROVED_WITH_ALERTS: 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300',
  REJECTED: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
  ESCALATED: 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300',
};

function Badge({ text, colorClass }: { text: string; colorClass: string }) {
  return (
    <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium', colorClass)}>
      {text}
    </span>
  );
}

function formatRelative(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function formatMs(ms: number): string {
  return ms < 60_000 ? `${Math.round(ms / 1000)}s` : `${Math.round(ms / 60_000)}m`;
}

// ─── Skeleton ───────────────────────────────────────────────────────────────

function TableSkeleton() {
  return (
    <div role="status" aria-label="Loading data" className="space-y-2">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="flex gap-4 rounded p-3 border border-border animate-pulse">
          <div className="h-4 w-40 rounded bg-muted" aria-hidden="true" />
          <div className="h-6 w-20 rounded-full bg-muted" aria-hidden="true" />
          <div className="h-6 w-24 rounded-full bg-muted" aria-hidden="true" />
          <div className="h-4 w-16 rounded bg-muted ml-auto" aria-hidden="true" />
        </div>
      ))}
    </div>
  );
}

// ─── Types ──────────────────────────────────────────────────────────────────

type SortKey = 'file_name' | 'status' | 'final_decision' | 'created_at';

interface RecentJobsTableProps {
  items: JobListItem[];
  total: number;
  page: number;
  onPageChange: (p: number) => void;
  sortKey: SortKey;
  sortDir: 'asc' | 'desc';
  onSort: (k: SortKey) => void;
  loading: boolean;
  error: Error | null;
  onRetry: () => void;
}

// ─── Component ───────────────────────────────────────────────────────────────

/**
 * Sortable, paginated job history table for the Overview tab.
 * Clicking a row navigates to the Job Detail page.
 */
export function RecentJobsTable({
  items, total, page, onPageChange,
  sortKey, sortDir, onSort,
  loading, error, onRetry,
}: RecentJobsTableProps) {
  const navigate = useNavigate();
  const limit = 10;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  if (loading) return <TableSkeleton />;

  if (error) {
    return (
      <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">
        <p className="font-medium">Failed to load jobs.</p>
        <button type="button" onClick={onRetry} className="mt-2 underline hover:no-underline">
          Retry
        </button>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <EmptyState
        title="No jobs yet. Submit your first contract."
        description="Once contracts are submitted for analysis, they will appear here."
      />
    );
  }

  function sortBtn(label: string, colKey: SortKey) {
    const active = sortKey === colKey;
    return (
      <button
        type="button"
        onClick={() => { onSort(colKey); }}
        aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
        className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
      >
        {label}
        <ArrowUpDown className={cn('h-3 w-3', active && 'text-foreground')} aria-hidden="true" />
      </button>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border border-border">
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-muted/40">
            <tr>
              <th scope="col" role="columnheader" className="px-4 py-3 text-left">
                {sortBtn('File', 'file_name')}
              </th>
              <th scope="col" role="columnheader" className="px-4 py-3 text-left">
                {sortBtn('Status', 'status')}
              </th>
              <th scope="col" role="columnheader" className="px-4 py-3 text-left">
                {sortBtn('AI Decision', 'final_decision')}
              </th>
              <th scope="col" role="columnheader" className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground">Review</th>
              <th scope="col" role="columnheader" className="px-4 py-3 text-left">
                {sortBtn('Submitted', 'created_at')}
              </th>
              <th scope="col" role="columnheader" className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground">Time</th>
              <th scope="col" role="columnheader" className="px-4 py-3 text-right text-xs font-semibold text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border bg-card">
            {items.map((job) => (
              <tr
                key={job.id}
                onClick={() => { navigate(`/jobs/${job.id}`); }}
                className="cursor-pointer hover:bg-muted/30 focus-within:bg-muted/30 transition-colors"
              >
                <td className="px-4 py-3 font-medium max-w-[180px] truncate" title={job.file_name}>
                  {job.file_name}
                </td>
                <td className="px-4 py-3">
                  <Badge text={jobStatusLabel[job.status]} colorClass={statusColors[job.status]} />
                </td>
                <td className="px-4 py-3">
                  {job.final_decision
                    ? <Badge text={finalDecisionLabel[job.final_decision]} colorClass={decisionColors[job.final_decision]} />
                    : <span className="text-muted-foreground">—</span>}
                </td>
                <td className="px-4 py-3">
                  {job.review_status
                    ? <span className="text-xs text-muted-foreground">{reviewStatusLabel[job.review_status]}</span>
                    : <span className="text-muted-foreground">—</span>}
                </td>
                <td
                  className="px-4 py-3 text-muted-foreground text-xs font-mono whitespace-nowrap"
                  title={formatRelative(job.created_at)}
                >
                  {fmtDateTime(job.created_at)}
                </td>
                <td className="px-4 py-3 text-muted-foreground text-xs">
                  {job.processing_time_ms != null ? formatMs(job.processing_time_ms) : '—'}
                </td>
                <td className="px-4 py-3 text-right">
                  <JobRowActions jobId={job.id} status={job.status} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-border bg-muted/20 px-4 py-3 text-xs text-muted-foreground">
          <span>Showing {(page - 1) * limit + 1}–{Math.min(page * limit, total)} of {total}</span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => { onPageChange(page - 1); }}
              disabled={page === 1}
              aria-label="Previous page"
              className="rounded p-1 hover:bg-accent disabled:opacity-40 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={() => { onPageChange(page + 1); }}
              disabled={page === totalPages}
              aria-label="Next page"
              className="rounded p-1 hover:bg-accent disabled:opacity-40 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              <ChevronRight className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
